import useProjectStore from '../../../ProjectCanvas/useProjectStore';
import useCanvasStore from '../useCanvasStore';
import { findNonOverlappingPosition } from '../utils/PositioningUtils';
import { v4 as uuid } from 'uuid';
import { EDGE_TYPES } from '../../../../utils/CanvasConstants';
import { createSaveEvent } from '../../../../utils/createSaveEvent';
import { getElements } from '../../../../utils/CanvasUtil';
import { saveCanvasEvent } from '../../../../utils/canvasApi';
import { styled, Tooltip, Button } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { CanvasContext } from '../WorkflowCanvas';
import { ComponentLimitReachedDialog } from '../../Dialogs/ComponentLimitReachedDialog';
import { useNavigate } from 'react-router-dom';
import {
  initializeComponent,
  markComponentInitializing,
  clearComponentInitializing,
} from '../../../../utils/ComponentInitializationUtil';

const DraggableItem = styled('div')(({ disabled }) => ({
  display: 'flex',
  marginBottom: '4px',
  cursor: disabled ? 'wait' : 'pointer',
  opacity: disabled ? 0.7 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
}));

const SideBarItemContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: '0',
  margin: '0',
  position: 'relative',
}));

const SideBarLogoContainer = styled('div')(() => ({
  marginRight: '12px',
  display: 'flex',
  alignItems: 'center',
}));

const Logo = styled('img')(() => ({
  height: '36px',
  transition: 'transform 0.2s ease',
}));

const DisplayNameText = styled('p')(({ theme }) => ({
  margin: '0',
  padding: '0',
  fontFamily: 'Open Sans',
  fontSize: '14px',
  lineHeight: '18px',
  color: theme.palette.text.primary,
  transition: 'color 0.2s ease',
}));

const DescriptionText = styled('p')(({ theme }) => ({
  margin: '2px 0 0 0',
  padding: '0',
  color: theme.palette.text.secondary,
  fontSize: '12px',
  fontFamily: 'Open Sans',
  lineHeight: '15px',
}));

const AiExclusionContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '0',
  right: '0',
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  padding: '0px 4px',
  height: '14px',
}));

const AiExclusionAsterisk = styled('span')(({ theme }) => ({
  fontSize: '0.6rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  lineHeight: 1,
}));

const SideBarLogo = props => {
  return (
    <SideBarLogoContainer>
      <Logo src={props.sidebarLogo || props.logo} alt={props.displayName || 'Component'} />
    </SideBarLogoContainer>
  );
};

const AiExclusionWarning = () => {
  return (
    <Tooltip title="This component cannot be modified by AI" placement="top" arrow>
      <AiExclusionContainer>
        <AiExclusionAsterisk>*</AiExclusionAsterisk>
      </AiExclusionContainer>
    </Tooltip>
  );
};

const SidebarItem = ({ classes, reactFlowInstance, workflowId, component }) => {
  const { edit, pendingConnectionSource: pendingConnectionSourceContext } =
    useContext(CanvasContext);

  const [isSaving, setIsSaving] = useState(false);
  const [pendingConnectionSource, setPendingConnectionSource] =
    pendingConnectionSourceContext || useState({});

  const [editComponent, setEditComponent] = edit || useState(null);

  const tier = useProjectStore.getState().tier;
  const maxComponents = tier === 'FREE' || tier === 'FREE_TRIAL' ? 50 : 250;

  const [openComponentLimitDialog, setOpenComponentLimitDialog] = useState(false);

  const navigate = useNavigate();

  const calculateCentrePosition = () => {
    try {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      return reactFlowInstance.screenToFlowPosition({
        x: screenWidth / 2,
        y: screenHeight / 2,
      });
    } catch (error) {
      console.error('[ComponentTray] Error calculating screen center, falling back:', error);
      return { x: 200, y: 200 };
    }
  };

  if (!component) {
    console.error('SidebarItem received invalid component (undefined)');
    return <></>;
  }

  const onDragStart = (event, component) => {
    event.dataTransfer.setData('application/reactflow', 'component');
    event.dataTransfer.setData('type', component.name);
    event.dataTransfer.effectAllowed = 'move';
  };

  const getNodesComponentCount = () => {
    return useCanvasStore.getState().nodes.filter(c => c.type === 'component').length;
  };

  const handleItemClick = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      if (!checkComponentLimits()) return;
      if (!validateReactFlowInstance()) return;

      const template = component;
      const position = determinePosition();
      const newNode = createNode(template, position);
      const newLink = createLink(newNode);

      // Mark as initializing before it renders so its InlineConfigPanel skips the doomed REST fetch
      // (the component isn't persisted until initializeComponent runs after the async save below).
      markComponentInitializing(newNode.data.type, newNode.id);

      applyChangesToCanvas(newNode, newLink);
      await saveChanges(newNode, newLink, position);

      const initialEditComponentData = {
        ...newNode.data,
        componentId: newNode.id,
        isLoading: false,
      };

      if (pendingConnectionSource && setEditComponent) {
        setEditComponent(initialEditComponentData);
      }
    } catch (error) {
      console.error('Error in handleItemClick:', error);
    } finally {
      setIsSaving(false);
      if (setPendingConnectionSource) {
        setPendingConnectionSource('');
      }
    }
  };

  const checkComponentLimits = () => {
    if (getNodesComponentCount() >= maxComponents) {
      setOpenComponentLimitDialog(true);
      setIsSaving(false);
      return false;
    }
    return true;
  };

  const validateReactFlowInstance = () => {
    if (!reactFlowInstance) {
      console.error('[ComponentTray] React Flow instance not available.');
      setIsSaving(false);
      return false;
    }
    return true;
  };

  const getPositionFromPendingConnection = (
    pendingConnectionSource,
    nodeSpacingX,
    nodeSpacingY
  ) => {
    const { id: sourceNodeId, position: sourceNodePosition } = pendingConnectionSource;

    const outgoingEdge = useCanvasStore.getState().edges.find(edge => edge.source === sourceNodeId);
    const targetNodeId = outgoingEdge ? outgoingEdge.target : '';

    const basePosition = {
      x: sourceNodePosition.x + nodeSpacingX,
      y: sourceNodePosition.y + nodeSpacingY,
    };

    return {
      position: basePosition,
      relativeToComponentId: targetNodeId,
      isVerticalStack: !!targetNodeId,
    };
  };

  const determinePosition = () => {
    const nodeSpacingX = 450;
    const nodeSpacingY = 0;
    const existingComponents = useCanvasStore.getState().nodes.filter(node => node.type === 'component');

    let initialPosition;
    let relativeToComponentId = '';
    let isVerticalStack = false;

    if (pendingConnectionSource?.id && pendingConnectionSource?.position) {
      const {
        position,
        relativeToComponentId: relativeId,
        isVerticalStack: isVertical,
      } = getPositionFromPendingConnection(pendingConnectionSource, nodeSpacingX, nodeSpacingY);

      initialPosition = position;
      relativeToComponentId = relativeId;
      isVerticalStack = isVertical;
    } else {
      initialPosition = calculateCentrePosition();
    }

    const componentDimensions = { width: 280, height: 54 };
    return findNonOverlappingPosition(
      initialPosition,
      existingComponents,
      componentDimensions,
      relativeToComponentId,
      isVerticalStack
    );
  };

  const createNode = (template, position) => {
    return {
      id: uuid(),
      type: 'component',
      position,
      data: {
        type: template.name,
        label: template.displayName,
        displayName: template.displayName,
        logo: template.logo,
        hasInput: template.hasInput ? template.hasInput : false,
        hasOutput: template.hasOutput ? template.hasOutput : false,
        baseColor: template.baseColor,
        schemaVersion: 2,
      },
    };
  };

  const createLink = newNode => {
    const template = component;
    const isTargetTrigger = template.isTrigger === true || template.hasInput === false;
    const canConnect = pendingConnectionSource && pendingConnectionSource.id && !isTargetTrigger;

    if (!canConnect) return null;

    return {
      id: uuid(),
      source: pendingConnectionSource.id,
      target: newNode.id,
      type: EDGE_TYPES.LINK,
      markerEnd: {
        type: 'arrowclosed',
        color: '#868686',
      },
      style: {
        strokeWidth: '3px',
        stroke: pendingConnectionSource.baseColor || '#868686',
      },
      label: 'componentLink',
    };
  };

  const applyChangesToCanvas = (newNode, newLink) => {
    useCanvasStore.getState().addNodes([newNode]);
    if (newLink) {
      useCanvasStore.getState().addEdges([newLink]);
    }
  };

  const saveChanges = async (newNode, newLink, position) => {
    try {
      let rfiObj = reactFlowInstance.toObject();
      let viewport = {
        x: rfiObj.viewport.x.toFixed(5),
        y: rfiObj.viewport.y.toFixed(5),
        zoom: rfiObj.viewport.zoom.toFixed(5),
      };

      let saveEventType;
      let eventData;

      if (newLink && pendingConnectionSource && pendingConnectionSource.id) {
        saveEventType = 'ADD_COMPONENT_AND_LINK';
        eventData = {
          sourceComponentId: pendingConnectionSource.id,
          targetComponentId: newNode.id,
          targetType: newNode.data.type,
          componentId: newNode.id,
          type: newNode.data.type,
          viewData: {
            x: position.x,
            y: position.y,
          },
        };
      } else {
        saveEventType = 'ADD_COMPONENT';
        eventData = {
          componentId: newNode.id,
          type: newNode.data.type,
          viewData: {
            x: position.x,
            y: position.y,
          },
        };
      }

      let saveEvent = createSaveEvent(
        saveEventType,
        workflowId,
        viewport,
        getElements(reactFlowInstance),
        eventData
      );

      await saveCanvasEvent(workflowId, saveEvent).then(response => {
        initializeComponent(newNode.data.type, newNode.id);
      });
    } catch (error) {
      // Save failed before initializeComponent could run; release the init guard so the node isn't
      // stuck in a loading state.
      clearComponentInitializing(newNode.data.type, newNode.id);
      console.error('Error saving component/link:', error);
      throw error;
    }
  };

  return (
    <>
      <ComponentLimitReachedDialog
        open={openComponentLimitDialog}
        maxComponentLimit={maxComponents}
        onCloseClick={() => {
          setOpenComponentLimitDialog(false);
        }}
        onUpgradeClick={() => {
          navigate('/organization');
          setOpenComponentLimitDialog(false);
        }}
      />
      <DraggableItem
        draggable={!isSaving}
        onDragStart={event => onDragStart(event, component)}
        onClick={() => handleItemClick()}
        className={classes?.canvasItem}
        disabled={isSaving}
      >
        <SideBarLogo
          sidebarLogo={component.sidebarLogo}
          logo={component.logo}
          displayName={component.displayName}
        />
        <SideBarItemContainer>
          {component.isExcluded && <AiExclusionWarning />}
          <div>
            <DisplayNameText>{component.displayName || 'Component'}</DisplayNameText>
          </div>
          <div>
            <DescriptionText>{component.description || ''}</DescriptionText>
          </div>
        </SideBarItemContainer>
      </DraggableItem>
    </>
  );
};
export default SidebarItem;
